import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Question } from '../../models/QAndA';
import { useTranslation } from 'react-i18next';
import QAndAContext from '../../providers/QAndA/QAndA.context';
import { PRIORITY_ICONS } from '../../providers/QAndA/QAndA.provider';
import { getUserFullName } from 'utils';
import usePastTime from 'hooks/usePastTime';
import { Button } from 'ncoded-component-library';
import useMatchMedia from 'hooks/useMatchMedia';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import { getCurrentBuyStateByStatus } from '../QAndARoleFilter/data';
import CircledExclamationmarkIcon from 'icons/CircledExclamationmark.icon';
import {
  isQARoleNone,
  isQABuyDrafter,
  isQuestionAnswerApproved,
  isQuestionDrafted,
  getQuestionSubject,
} from '../../utils';
import ShowShortenedRichText from '../ShowShortenedRichText';
import RelatedFileQa from '../RelatedFileQa';
import { QAndAMobileCard } from './components/QAndAMobileCard';
import useManageQuestion from './hooks/useManageQuestion';
import ProductsIcon from 'icons/Products.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import PluscircleIcon from 'icons/Pluscircle.icon';
import useQueryParams from 'hooks/useQueryParams';
import { NewTag } from '../NewTag';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import FaqTag from '../FaqTag';
import { FIRST_PAGE } from 'data/global';

import './QAndABuyerContainer.styles.scss';

type QAndABuyerContainerProps = {
  className?: string;
};

const TABLE_COLUMNS = [
  'question',
  'subject',
  'priority',
  'draftedBy',
  'drafted',
] as const;

const FOR_DRAFTER_ROLE = 'status' as const;

const MANAGE_TABLE_COLUMNS = [...TABLE_COLUMNS, 'manage'] as const;

const TRANSLATE_HEADING_CELL = [
  'question',
  'subject',
  'draftedBy',
  'drafted',
  'manage',
  FOR_DRAFTER_ROLE,
];

const SORT_COL_KEYS = {
  question: '',
  subject: 'sortSubject',
  manage: '',
  priority: 'priorityIndex',
  draftedBy: '',
  drafted: 'createdAt',
};
const shouldTranslateHeading = (key: string) =>
  TRANSLATE_HEADING_CELL.includes(key);

const HEADING_CELL_CONTENT: Record<
  typeof MANAGE_TABLE_COLUMNS[number],
  React.ReactNode
> = {
  priority: <CircledExclamationmarkIcon />,
  question: 'question',
  subject: 'subject',
  draftedBy: 'draftedBy',
  drafted: 'drafted',
  manage: 'manage',
};

const BODY_CONTENT_GENERATE: Record<
  typeof MANAGE_TABLE_COLUMNS[number] | 'status',
  (
    val: Question,
    updateQuestion: (question: Partial<Question>) => void,
  ) => React.ReactNode
> = {
  subject: ({ subject, otherSubject }: Question) => (
    <div className="dr-subject-cell">
      <p>{getQuestionSubject(subject, otherSubject)}</p>
    </div>
  ),
  priority: (question: Question) => PRIORITY_ICONS[question.priority],
  question: (question, updateQuestion) => (
    <SubjectCellComponent question={question} updateQuestion={updateQuestion} />
  ),
  draftedBy: (question: Question) => (
    <DraftedByCellComponent question={question} />
  ),
  drafted: (question: Question) => <DraftedCellComponent question={question} />,
  manage: (question: Question) => <ManageCellComponent question={question} />,
  status: (question: Question) => <StatusCellComponent question={question} />,
};

const QAndABuyerContainer: React.FC<QAndABuyerContainerProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { displayedQuestions, updateQuestion } = useContext(QAndAContext);
  const { qaRole } = useContext(DataRoomContext);
  const {
    setQueryParam,
    params: { sort },
  } = useQueryParams<{ sort: string; page: string }>();

  const isTablet = useMatchMedia('isTablet');

  const isQuestionsListEmpty = !displayedQuestions.length;

  const canManage = useMemo(() => !isQARoleNone(qaRole), [qaRole]);

  const isDrafter = useMemo(() => isQABuyDrafter(qaRole), [qaRole]);

  const classes = classNames('q-and-a-buyer-container', className);

  const tableColumns = useMemo(
    () => (canManage ? MANAGE_TABLE_COLUMNS : TABLE_COLUMNS),
    [canManage],
  );

  const TableHeading = useMemo(
    () => (
      <thead>
        <tr>
          {tableColumns.map((col) => {
            let content =
              isDrafter && col === 'draftedBy'
                ? FOR_DRAFTER_ROLE
                : HEADING_CELL_CONTENT[col];

            const sortingVal = SORT_COL_KEYS[col];
            if (shouldTranslateHeading(col)) {
              content = t(content as string);
            }

            const isSelected = sortingVal && sort?.includes(sortingVal);

            return (
              <th key={col} className={col}>
                <div
                  className={classNames({
                    selected: isSelected,
                    rotated: sort !== sortingVal,
                  })}
                >
                  {content}
                  {sortingVal && (
                    <Button
                      onClick={() => {
                        if (sort === sortingVal) {
                          setQueryParam('sort', `-${sortingVal}`);
                        } else {
                          setQueryParam('sort', sortingVal);
                        }
                        setQueryParam('page', FIRST_PAGE);
                      }}
                      className={classNames({
                        selected: isSelected,
                        rotated: sort === sortingVal,
                      })}
                    >
                      {isSelected ? (
                        <ArrowdownIcon />
                      ) : (
                        <>
                          <ChevrondownIcon /> <ChevrondownIcon />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [isDrafter, tableColumns, sort, t, setQueryParam],
  );

  if (isQuestionsListEmpty) return null;

  if (isTablet)
    return (
      <div className={classes}>
        {displayedQuestions.map((el) => (
          <QAndAMobileCard
            key={el._id}
            question={el}
            isManagingRole={canManage}
          />
        ))}
      </div>
    );

  return (
    <table className={classes}>
      {TableHeading}
      <tbody>
        {displayedQuestions.map((q) => (
          <tr key={q._id} className={classNames('row', q.status)}>
            {tableColumns.map((column) => {
              const col =
                isDrafter && column === 'draftedBy' ? FOR_DRAFTER_ROLE : column;

              return (
                <td key={col} className={col}>
                  {BODY_CONTENT_GENERATE[col](q, updateQuestion)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default QAndABuyerContainer;

type CellProps = {
  question: Question;
  updateQuestion?: (question: Partial<Question>) => void;
};

export const SubjectCellComponent: React.FC<CellProps> = ({
  question,
  updateQuestion,
}) => {
  const {
    subject,
    content,
    otherSubject,
    createdAt,
    status,
    isPublishedToFAQ,
    dataRoomLatestFollowupQuestion,
    dataRoomPreviousFollowupQuestion,
    dataRoomParentQuestion,
    dataRoomAnswer,
  } = question;

  const { t } = useTranslation();
  const isTablet = useMatchMedia('isTablet');

  const followUpQuestion =
    dataRoomLatestFollowupQuestion ||
    dataRoomPreviousFollowupQuestion ||
    dataRoomParentQuestion;

  return (
    <div className="subject-cell">
      {!!followUpQuestion && (
        <div className="subject-cell__follow-up">
          <span>
            {t(dataRoomLatestFollowupQuestion ? 'followUpTo' : 'followUpFrom')}
          </span>
          <ShowShortenedRichText
            question={question}
            maxHeight={60}
            asAccordion
            updateQuestion={updateQuestion}
          >
            {followUpQuestion?.content}
          </ShowShortenedRichText>
        </div>
      )}

      <div className="subject-cell__question-wrapper">
        <ShowShortenedRichText
          question={question}
          className="subject-cell__question-shortened"
          maxHeight={120}
          asAccordion
          updateQuestion={updateQuestion}
        >
          {content}
        </ShowShortenedRichText>

        {isQuestionDrafted(status) && <NewTag time={createdAt} />}
        {isPublishedToFAQ && <FaqTag />}
      </div>

      {dataRoomAnswer?.content && isQuestionAnswerApproved(status) && (
        <div className="subject-cell__answer-shortened">
          <ShowShortenedRichText
            question={question}
            maxHeight={120}
            asAccordion
            updateQuestion={updateQuestion}
          >
            {question.dataRoomAnswer.content}
          </ShowShortenedRichText>
        </div>
      )}

      <div className="tags">
        {isTablet && (
          <p>
            <ProductsIcon />
            {getQuestionSubject(subject, otherSubject)}
          </p>
        )}

        <RelatedFileQa question={question} />
      </div>
    </div>
  );
};

const StatusCellComponent: React.FC<CellProps> = ({ question }) => {
  const { t } = useTranslation();
  const copy = t(getCurrentBuyStateByStatus(question.status));
  return <div>{copy}</div>;
};

export const DraftedByCellComponent: React.FC<CellProps> = ({ question }) => {
  const { firstName, lastName } = question?.user || {};

  return <div>{getUserFullName(firstName, lastName)}</div>;
};

export const DraftedCellComponent: React.FC<CellProps> = ({ question }) => {
  const { createdAt } = question;

  const pastTime = usePastTime()(createdAt);

  if (!pastTime) return null;

  return <div className="dr-drafted-cell">{pastTime}</div>;
};

const ManageCellComponent: React.FC<CellProps> = ({ question }) => {
  const { doesntHaveAccess } = useContext(DataRoomContext);
  const {
    manageButtonText,
    followUpButtonText,
    cantManageQuestion,
    canFollowUp,
    YourDrawer,
    onManageClick,
    onFollowUpClick,
  } = useManageQuestion(question);

  if (doesntHaveAccess) return null;

  if (cantManageQuestion)
    return (
      <Button variant="link" disabled>
        {manageButtonText}
      </Button>
    );

  return (
    <>
      <Button variant="link" onClick={onManageClick}>
        {manageButtonText}
      </Button>

      {canFollowUp && (
        <Button
          className="q-and-a-follow-up"
          variant="link"
          icon={<PluscircleIcon />}
          onClick={onFollowUpClick}
        >
          {followUpButtonText}
        </Button>
      )}

      {YourDrawer}
    </>
  );
};
